import {
  Button,
  Menu,
  MenuCheckedValueChangeData,
  MenuCheckedValueChangeEvent,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  ToggleButton,
  Tooltip,
} from "@fluentui/react-components";
import {
  Bot16Regular,
  ChevronDown20Regular,
  Document16Regular,
  TargetEdit16Regular,
} from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { LLMType } from "models/enums/LLMType";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { normalizeModelKey } from "utils/generalUtils";
import {
  applySavedLlmSettingsMigration,
  markModelMigrationsComplete,
  remapLegacyModelName,
  STORAGE_KEY_USER_LLM_SETTINGS,
} from "utils/llmModelMigration";
import { v4 as uuidv4 } from "uuid";
import { AllowedRagCategory, setNewConfigForKey } from "../../redux/llmConfig";
import { enqueueToast } from "../../redux/toastSlice";
import { useStyles } from "./AIModelSettings.styles";
import { IAIModelSettingsProps } from "./AIModelSettings.types";

const TOOLTIP_MODEL = "Choose which AI model will respond to your questions.";
const LABEL_LOADING_MODEL = "Loading Model...";
const LABEL_MODEL_TRIGGER = "AI model";
const LABEL_MODEL_ANNOUNCEMENT = "Model:";
const WEB_SEARCH_PATTERN = /web search/i;
const MESSAGE_MODEL_DEFAULT_CHANGED_SUFFIX =
  "is now your default model. You can change it in the model menu.";

// UI label renamed to "Qualifications" (US-9770); internal quals/QUALS naming left as-is across the codebase
const QUALS_LABEL = "Qualifications";
const PROPOSALS_LABEL = "Proposals";
const CITATIONS_LABEL = "Citations";

const getQualsTooltip = (on: boolean) =>
  on
    ? "Click button to disable Qualifications"
    : "Click button to enable Qualifications";

const getProposalsTooltip = (on: boolean) =>
  on ? "Click button to disable Proposals" : "Click button to enable Proposals";

const getCitationsTooltip = (on: boolean) =>
  on ? "Click button to disable Citations" : "Click button to enable Citations";

const byOrderAsc = (a: IAccessLLMModel, b: IAccessLLMModel) =>
  (a.order ?? 0) - (b.order ?? 0);

const findModelByName = (models: IAccessLLMModel[], name?: string) => {
  const target = normalizeModelKey(name ?? ConstantValues.EMPTY_STRING);
  return models.find(
    (m) => normalizeModelKey(m.name ?? ConstantValues.EMPTY_STRING) === target,
  );
};

// A legacy name (Sonnet 4.5, Opus 4.5 / 4.6 / 4.7, Haiku 4.5) that is no longer available
// resolves to its replacement (when visible) before any lowest-order fallback.
const findModelOrReplacement = (models: IAccessLLMModel[], name?: string) =>
  findModelByName(models, name) ??
  findModelByName(models, remapLegacyModelName(name ?? "", models));

const findModelById = (models: IAccessLLMModel[], id?: string) => {
  const target = normalizeModelKey(id ?? ConstantValues.EMPTY_STRING);
  return models.find(
    (m) => normalizeModelKey(m.id ?? ConstantValues.EMPTY_STRING) === target,
  );
};

// Falls back to the first model when none has a numeric order, so the default
// and the displayed fallback always agree and never stay on "Loading Model...".
const getFirstOrderedModel = (models: IAccessLLMModel[]) =>
  [...models].filter((m) => typeof m.order === "number").sort(byOrderAsc)[0] ??
  models[0];

// Drops suffixes such as "- Problem Solver" but keeps "Web Search" visible.
const getShortModelName = (displayName: string): string =>
  WEB_SEARCH_PATTERN.test(displayName)
    ? displayName.trim()
    : displayName.split("-")[0].trim();

const getSelectedModel = (
  models: IAccessLLMModel[],
  llmType: string | { name?: string },
) => {
  const typeName =
    typeof llmType === "string"
      ? llmType
      : (llmType?.name ?? ConstantValues.EMPTY_STRING);

  const match = findModelOrReplacement(models, typeName);
  if (match) {
    return { key: match.id, name: match.name, displayName: match.displayName };
  }

  // Unknown or no longer available model: show the default instead of a raw string.
  const fallbackModel = getFirstOrderedModel(models);
  if (fallbackModel) {
    return {
      key: fallbackModel.id,
      name: fallbackModel.name,
      displayName: fallbackModel.displayName,
    };
  }

  return {
    key: LABEL_LOADING_MODEL,
    name: LABEL_LOADING_MODEL,
    displayName: LABEL_LOADING_MODEL,
  };
};

const pickEffectiveLlmType = (
  configLlmType: string | undefined,
  models: IAccessLLMModel[],
  savedRaw: string | null,
): string => {
  if (configLlmType) {
    return configLlmType.toLowerCase();
  }

  if (savedRaw) {
    try {
      const saved = JSON.parse(savedRaw);
      if (saved?.llmType) {
        return String(saved.llmType).toLowerCase();
      }
    } catch (e) {
      console.log("Error parsing saved LLM settings from localStorage", e);
    }
  }

  const first = getFirstOrderedModel(models);
  return (first?.name ?? LLMType.CLAUDE).toLowerCase();
};

const isAllowedCategory = (c: AllowedRagCategory): boolean =>
  c === "Qualifications" || c === "Proposals";

const isClaudeModel = (name?: string): boolean => {
  const n = (name ?? "").toLowerCase();
  return (
    n.includes("claude") ||
    n.includes("haiku") ||
    n.includes("opus") ||
    n.includes("sonnet")
  );
};

export const AIModelSettings = (props: IAIModelSettingsProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const isCompact = props.variant === "compact";

  const models: IAccessLLMModel[] = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const defaultModel = useMemo<IAccessLLMModel>(() => {
    return (
      getFirstOrderedModel(models) ?? {
        id: LLMType.CLAUDE,
        name: LLMType.CLAUDE,
        displayName: LLMType.CLAUDE,
      }
    );
  }, [models]);

  const currentLLMConfig = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[props.pageName] ?? {
        llmType: undefined,
        rag: false,
        citate: false,
        sourceRagCategories: [] as AllowedRagCategory[],
      },
  );

  const [selectedModel, setSelectedModel] = useState(() =>
    getSelectedModel(models, currentLLMConfig.llmType),
  );
  const [modelAnnouncement, setModelAnnouncement] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [citationEnabled, setCitationEnabled] = useState<boolean>(
    currentLLMConfig.citate ?? false,
  );
  const [qualsEnabled, setQualsEnabled] = useState<boolean>(
    (currentLLMConfig.sourceRagCategories ?? []).includes("Qualifications"),
  );
  const [proposalsEnabled, setProposalsEnabled] = useState<boolean>(
    (currentLLMConfig.sourceRagCategories ?? []).includes("Proposals"),
  );

  const checkedValues = useMemo(
    () => ({
      modelSelection: selectedModel?.key
        ? [normalizeModelKey(selectedModel.key)]
        : [],
    }),
    [selectedModel],
  );

  useEffect(() => {
    setCitationEnabled(currentLLMConfig.citate ?? false);
  }, [currentLLMConfig.citate]);

  useEffect(() => {
    const ragCategories: string[] = currentLLMConfig.sourceRagCategories ?? [];
    setQualsEnabled(ragCategories.includes("Qualifications"));
    setProposalsEnabled(ragCategories.includes("Proposals"));
  }, [currentLLMConfig.sourceRagCategories]);

  // Layout effect so a remapped model is shown before the first paint.
  useLayoutEffect(() => {
    if (!models || models.length === 0) return;

    // One-time remap of a saved legacy selection (Sonnet 4.5 -> 5.5, Opus 4.x ->
    // 4.8), only once the user can see the replacement; otherwise untouched.
    const migration =
      props.pageName === ConstantValues.CHAT
        ? applySavedLlmSettingsMigration(models, currentUser.id)
        : undefined;
    const savedRaw = migration?.savedRaw ?? null;

    // When the saved value was remapped, keep the Redux config in agreement
    // (it takes precedence over storage when picking the effective model).
    const configLlmType =
      migration?.hasRemapped && currentLLMConfig.llmType
        ? remapLegacyModelName(currentLLMConfig.llmType, models)
        : currentLLMConfig.llmType;

    if (migration?.hasRemapped) {
      const migratedModel = findModelByName(models, migration.migratedLlmType);
      dispatch(
        enqueueToast({
          id: uuidv4(),
          intent: "info",
          message: `${migratedModel?.displayName ?? migration.migratedLlmType} ${MESSAGE_MODEL_DEFAULT_CHANGED_SUFFIX}`,
        }),
      );
    }

    const effective = pickEffectiveLlmType(configLlmType, models, savedRaw);
    const resolved = findModelOrReplacement(models, effective) ?? defaultModel;

    setSelectedModel({
      key: resolved.id,
      name: resolved.name,
      displayName: resolved.displayName,
    });

    const nextLlm = (
      resolved.name ??
      resolved.id ??
      LABEL_LOADING_MODEL
    ).toLowerCase();
    const currentLlm = (
      currentLLMConfig.llmType ?? ConstantValues.EMPTY_STRING
    ).toLowerCase();

    let savedCitate: boolean | undefined;
    let savedCategories: AllowedRagCategory[] | undefined;
    if (props.pageName === ConstantValues.CHAT && savedRaw) {
      try {
        const saved = JSON.parse(savedRaw);
        savedCitate =
          typeof saved?.citate === "boolean" ? saved.citate : undefined;
        savedCategories = (saved?.sourceRagCategories ?? []).filter(
          isAllowedCategory,
        );
      } catch (e) {
        console.log("Error parsing saved LLM settings from localStorage", e);
      }
    }

    const finalCitate = currentLLMConfig.citate ?? savedCitate ?? false;
    const finalCategories = currentLLMConfig.sourceRagCategories?.length
      ? currentLLMConfig.sourceRagCategories
      : (savedCategories ?? []);
    const finalRag = finalCategories.length > 0;

    if (
      nextLlm !== currentLlm ||
      (currentLLMConfig.citate ?? false) !== finalCitate ||
      JSON.stringify(currentLLMConfig.sourceRagCategories ?? []) !==
        JSON.stringify(finalCategories)
    ) {
      dispatch(
        setNewConfigForKey({
          key: props.pageName,
          config: {
            ...currentLLMConfig,
            llmType: nextLlm,
            citate: finalCitate,
            rag: finalRag,
            sourceRagCategories: finalCategories,
          },
        }),
      );
    }
  }, [
    props.pageName,
    models,
    defaultModel,
    currentUser.id,
    currentLLMConfig.llmType,
    currentLLMConfig.citate,
    currentLLMConfig.sourceRagCategories,
    dispatch,
  ]);

  const persistCategories = (categories: AllowedRagCategory[]) => {
    const allowed = categories.filter(isAllowedCategory);
    const rag = allowed.length > 0;

    dispatch(
      setNewConfigForKey({
        key: props.pageName,
        config: { ...currentLLMConfig, rag, sourceRagCategories: allowed },
      }),
    );

    if (props.pageName === ConstantValues.CHAT) {
      const saved = JSON.parse(
        localStorage.getItem(STORAGE_KEY_USER_LLM_SETTINGS) || "{}",
      );
      localStorage.setItem(
        STORAGE_KEY_USER_LLM_SETTINGS,
        JSON.stringify({
          ...saved,
          userId: currentUser.id,
          llmType: (
            currentLLMConfig.llmType ?? defaultModel.name
          ).toLowerCase(),
          rag,
          citate: currentLLMConfig.citate ?? false,
        }),
      );
    }
  };

  const handleModelChange = (
    _e: MenuCheckedValueChangeEvent,
    data: MenuCheckedValueChangeData,
  ) => {
    const selectedId = data.checkedItems?.[0];
    if (!selectedId) {
      return;
    }

    const model = findModelById(models, selectedId);
    if (!model) {
      return;
    }

    setModelAnnouncement(`${LABEL_MODEL_ANNOUNCEMENT} ${model.displayName}`);
    if (props.pageName === ConstantValues.CHAT) {
      // An explicit pick must never be remapped by the one-time migration.
      markModelMigrationsComplete(models, currentUser.id);
    }

    const nextSelected = {
      key: model.id,
      name: model.name,
      displayName: model.displayName,
    };
    setSelectedModel(nextSelected);

    if (props.pageName === ConstantValues.CHAT) {
      const currentSaved = JSON.parse(
        localStorage.getItem(STORAGE_KEY_USER_LLM_SETTINGS) || "{}",
      );
      localStorage.setItem(
        STORAGE_KEY_USER_LLM_SETTINGS,
        JSON.stringify({
          ...currentSaved,
          userId: currentUser.id,
          llmType: (model.name ?? ConstantValues.EMPTY_STRING).toLowerCase(),
        }),
      );
    }

    dispatch(
      setNewConfigForKey({
        key: props.pageName,
        config: {
          ...currentLLMConfig,
          llmType: (model.name ?? ConstantValues.EMPTY_STRING).toLowerCase(),
        },
      }),
    );
  };

  const handleToggleCitations = (checked?: boolean) => {
    const next = !!checked;
    setCitationEnabled(next);
    dispatch(
      setNewConfigForKey({
        key: props.pageName,
        config: { ...currentLLMConfig, citate: next },
      }),
    );

    if (props.pageName === ConstantValues.CHAT) {
      const saved = JSON.parse(
        localStorage.getItem(STORAGE_KEY_USER_LLM_SETTINGS) || "{}",
      );
      localStorage.setItem(
        STORAGE_KEY_USER_LLM_SETTINGS,
        JSON.stringify({
          ...saved,
          userId: currentUser.id,
          citate: next,
        }),
      );
    }
  };

  const toggleQuals = (on: boolean) => {
    setQualsEnabled(on);
    const next = new Set<AllowedRagCategory>(
      currentLLMConfig.sourceRagCategories ?? [],
    );
    const isOn = on
      ? next.add("Qualifications")
      : next.delete("Qualifications");
    persistCategories(Array.from(next));
  };

  const toggleProposals = (on: boolean) => {
    setProposalsEnabled(on);
    const next = new Set<AllowedRagCategory>(
      currentLLMConfig.sourceRagCategories ?? [],
    );
    const isOn = on ? next.add("Proposals") : next.delete("Proposals");
    persistCategories(Array.from(next));
  };

  const modelAnnouncementRegion = (
    <span
      role="status"
      aria-live="polite"
      aria-busy={models.length === 0}
      className={styles.visuallyHidden}
    >
      {modelAnnouncement}
    </span>
  );

  if (isCompact) {
    const isClaude = isClaudeModel(selectedModel.name);
    return (
      <div className={styles.compactContainer}>
        {modelAnnouncementRegion}
        <Menu onCheckedValueChange={handleModelChange}>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content={TOOLTIP_MODEL} relationship="description">
              <Button
                appearance="transparent"
                className={styles.compactButton}
                aria-label={`${LABEL_MODEL_TRIGGER}: ${selectedModel.displayName}`}
              >
                <span
                  className={isClaude ? styles.dotClaude : styles.dotOpenai}
                  aria-hidden="true"
                />
                <span>{getShortModelName(selectedModel.displayName)}</span>
                <ChevronDown20Regular
                  className={styles.compactChevron}
                  aria-hidden="true"
                />
              </Button>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover className={styles.menuPopover}>
            <MenuList checkedValues={checkedValues}>
              {[...models].sort(byOrderAsc).map((model) => {
                const isSelected =
                  normalizeModelKey(model.id) ===
                  normalizeModelKey(selectedModel.key);
                return (
                  <MenuItemRadio
                    key={normalizeModelKey(model.id)}
                    value={normalizeModelKey(model.id)}
                    name="modelSelection"
                    className={mergeClasses(
                      styles.menuItem,
                      isSelected && styles.menuItemSelected,
                    )}
                  >
                    {model.displayName || LABEL_LOADING_MODEL}
                  </MenuItemRadio>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>

        <div className={styles.compactSpacer} />

        <Tooltip content={getQualsTooltip(qualsEnabled)} relationship="label">
          <ToggleButton
            appearance="secondary"
            checked={qualsEnabled}
            icon={<TargetEdit16Regular aria-hidden="true" />}
            onClick={() => toggleQuals(!qualsEnabled)}
            className={mergeClasses(
              styles.nhToggle,
              styles.nhToggleCompact,
              qualsEnabled && styles.nhTogglePressed,
            )}
          >
            {QUALS_LABEL}
          </ToggleButton>
        </Tooltip>

        <Tooltip
          content={getProposalsTooltip(proposalsEnabled)}
          relationship="label"
        >
          <ToggleButton
            appearance="secondary"
            checked={proposalsEnabled}
            icon={<Document16Regular aria-hidden="true" />}
            onClick={() => toggleProposals(!proposalsEnabled)}
            className={mergeClasses(
              styles.nhToggle,
              styles.nhToggleCompact,
              proposalsEnabled && styles.nhTogglePressed,
            )}
          >
            {PROPOSALS_LABEL}
          </ToggleButton>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {modelAnnouncementRegion}
      <div className={styles.innerContainer}>
        <Menu onCheckedValueChange={handleModelChange}>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content={TOOLTIP_MODEL} relationship="description">
              <Button
                shape="circular"
                appearance="secondary"
                className={styles.botButton}
                aria-label={`${LABEL_MODEL_TRIGGER}: ${selectedModel.displayName}`}
              >
                <Bot16Regular aria-hidden="true" />
                <span>{getShortModelName(selectedModel.displayName)}</span>
              </Button>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover className={styles.menuPopover}>
            <MenuList checkedValues={checkedValues}>
              {[...models].sort(byOrderAsc).map((model) => {
                const isSelected =
                  normalizeModelKey(model.id) ===
                  normalizeModelKey(selectedModel.key);
                return (
                  <MenuItemRadio
                    key={normalizeModelKey(model.id)}
                    value={normalizeModelKey(model.id)}
                    name="modelSelection"
                    className={mergeClasses(
                      styles.menuItem,
                      isSelected && styles.menuItemSelected,
                    )}
                  >
                    {model.displayName || LABEL_LOADING_MODEL}
                  </MenuItemRadio>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
      {(props.pageName === ConstantValues.CHAT ||
        props.pageName === ConstantValues.TEMPORARY_CHAT) && (
        <>
          <Tooltip content={getQualsTooltip(qualsEnabled)} relationship="label">
            <div className={styles.switch}>
              <ToggleButton
                shape="circular"
                appearance="secondary"
                checked={qualsEnabled}
                onClick={() => toggleQuals(!qualsEnabled)}
                className={mergeClasses(
                  styles.nhToggle,
                  qualsEnabled && styles.nhTogglePressed,
                )}
              >
                {QUALS_LABEL}
              </ToggleButton>
            </div>
          </Tooltip>

          <Tooltip
            content={getProposalsTooltip(proposalsEnabled)}
            relationship="label"
          >
            <div className={styles.switch}>
              <ToggleButton
                shape="circular"
                appearance="secondary"
                checked={proposalsEnabled}
                onClick={() => toggleProposals(!proposalsEnabled)}
                className={mergeClasses(
                  styles.nhToggle,
                  proposalsEnabled && styles.nhTogglePressed,
                )}
              >
                {PROPOSALS_LABEL}
              </ToggleButton>
            </div>
          </Tooltip>

          <Tooltip
            content={getCitationsTooltip(citationEnabled)}
            relationship="label"
          >
            <div className={styles.switch}>
              <ToggleButton
                shape="circular"
                appearance="secondary"
                checked={citationEnabled}
                onClick={() => handleToggleCitations(!citationEnabled)}
                className={mergeClasses(
                  styles.nhToggle,
                  citationEnabled && styles.nhTogglePressed,
                )}
              >
                {CITATIONS_LABEL}
              </ToggleButton>
            </div>
          </Tooltip>
        </>
      )}
    </div>
  );
};
